// =============================================================================
// Cadastro de linha orçamentária — a tela que fecha o critério de aceite
//
// "O Financeiro deve conseguir CADASTRAR e acompanhar o orçamento de forma
// consolidada. Na sequência, deve conseguir iniciar o lançamento manual do
// orçamento de 2027." (AF-01 §13)
//
// Campos e comportamento vêm da Especificação §2, escrita pela Mariana, que é a
// fonte do AF-01. Nada aqui é invenção de tela: a ordem dos campos, o que abre
// o quê e os textos de apoio saem de lá.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, buscarReceita, FalhaApi, type LinhaMes } from "../lib/api.ts";
import { competenciaLonga, moeda } from "../lib/formato.ts";
import { CampoDinheiro } from "../componentes/CampoDinheiro.tsx";
import { CabecalhoGrafico, CampoCompetencia, Painel, Pilula, Vazio } from "../componentes/ui.tsx";
import { ROTULO_STATUS, TOM_STATUS } from "../lib/formato.ts";
import { ContratoOrcado } from "./ContratoOrcado.tsx";

type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string; natureza: string; pai_id: string | null }[];
  fornecedores: { id: string; nome: string }[];
  exercicios: { ano: number; status: string }[];
};

type Despesa = { nome: string; centavos: string | null; fornecedorId: string };

/**
 * Os dois cenários do cadastro. Receita existe desde a migration 043.
 *
 * ⚠️ O cenário escolhe as NATUREZAS de categoria que aparecem, e é só isso que
 * separa uma linha de receita de uma de despesa: a `linha_orcamentaria` não tem
 * natureza própria, ela vem da categoria. Despesa inclui investimento porque os
 * dois são saída e o modelo do orçamento orça os dois lado a lado.
 */
type Cenario = "despesa" | "receita";

const CENARIOS: {
  valor: Cenario;
  rotulo: string;
  naturezas: { valor: string; rotulo: string }[];
}[] = [
  {
    valor: "despesa",
    rotulo: "Despesa",
    naturezas: [
      { valor: "despesa", rotulo: "Despesa" },
      { valor: "investimento", rotulo: "Investimento" },
    ],
  },
  {
    valor: "receita",
    rotulo: "Receita",
    naturezas: [{ valor: "receita", rotulo: "Receita" }],
  },
];

const TIPOS: { valor: string; rotulo: string; cenario: Cenario; ajuda: string }[] = [
  { valor: "pontual", rotulo: "Pontual / ação", cenario: "despesa", ajuda: "Gasto único, sem recorrência. Ação de Dia dos Pais, treinamento avulso." },
  { valor: "recorrente", rotulo: "Recorrente", cenario: "despesa", ajuda: "Gera as competências futuras na frequência definida. Assinatura, mensalidade, seguro." },
  { valor: "investimento", rotulo: "Investimento", cenario: "despesa", ajuda: "Gasto de capital, tratado separado da despesa operacional. Equipamento, licença perpétua." },
  // Investimento é gasto de capital: não existe como tipo de linha de receita.
  { valor: "pontual", rotulo: "Pontual", cenario: "receita", ajuda: "Entrada única, sem recorrência. Uma turma avulsa, um contrato B2B fechado." },
  { valor: "recorrente", rotulo: "Recorrente", cenario: "receita", ajuda: "Gera as competências futuras na frequência definida. Mensalidade de curso, contrato recorrente." },
];

const FREQUENCIAS = [
  { valor: "mensal", rotulo: "Mensal", regra: "Mês exato: o lançamento precisa cair na mesma competência." },
  { valor: "trimestral", rotulo: "Trimestral", regra: "Janela de competência: casa qualquer lançamento dentro do intervalo." },
  { valor: "semestral", rotulo: "Semestral", regra: "Janela de competência." },
  { valor: "anual", rotulo: "Anual", regra: "Janela de competência." },
] as const;

function competenciaCorrente(): string {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, "0")}-01`;
}

export function CadastroOrcamento({ competencia }: { competencia: string }) {
  const qc = useQueryClient();
  const cadastros = useQuery({ queryKey: ["cadastros"], queryFn: () => api.get<Cadastros>("/api/cadastros") });

  const [cenario, setCenario] = useState<Cenario>("despesa");
  const [centroCustoId, setCentroCustoId] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [tipo, setTipo] = useState<string>("pontual");
  const [comp, setComp] = useState(() => competencia || competenciaCorrente());
  const [valor, setValor] = useState<string | null>(null);
  const [fornecedorId, setFornecedorId] = useState("");

  const [recorrencia, setRecorrencia] = useState<string>("mensal");
  const [renovacao, setRenovacao] = useState<string>("automatica");
  const [proximaRenovacao, setProximaRenovacao] = useState("");

  const [temVariavel, setTemVariavel] = useState(false);
  const [fixo, setFixo] = useState<string | null>(null);
  const [variavel, setVariavel] = useState<string | null>(null);
  const [pctFixo, setPctFixo] = useState("30");
  const [pctVariavel, setPctVariavel] = useState("80");

  const [multiplas, setMultiplas] = useState(false);
  const [despesas, setDespesas] = useState<Despesa[]>([{ nome: "", centavos: null, fornecedorId: "" }]);

  const exercicio = Number.parseInt(comp.slice(0, 4), 10);
  const ehReceita = cenario === "receita";
  const naturezas = CENARIOS.find((x) => x.valor === cenario)!.naturezas;
  const tipos = TIPOS.filter((t) => t.cenario === cenario);

  // Com múltiplas despesas, o teto da linha é a SOMA das ações — digitar os dois
  // deixaria dois números discordando na mesma tela (Especificação §2.7).
  const totalDespesas = useMemo(
    () => despesas.reduce((s, d) => s + BigInt(d.centavos || "0"), 0n).toString(),
    [despesas],
  );
  const valorEfetivo = multiplas ? totalDespesas : valor;

  // A lista lateral lê fontes diferentes por cenário: a execução por linha
  // exclui receita desde a 043, então a receita vem da rota própria dela.
  const linhasDoMes = useQuery({
    queryKey: ["execucao-linhas", comp],
    queryFn: () => api.get<{ linhas: LinhaMes[] }>(`/api/execucao/linhas?competencia=${comp}&limit=200`),
    enabled: !ehReceita,
  });
  const receitaDoMes = useQuery({
    queryKey: ["receita", [comp]],
    queryFn: () => buscarReceita([comp]),
    enabled: ehReceita,
  });

  const salvar = useMutation({
    mutationFn: () =>
      api.post<{ linha_id: string }>("/api/linhas", {
        exercicio_ano: exercicio,
        centro_custo_id: centroCustoId,
        categoria_id: categoriaId,
        tipo,
        valor_centavos: valorEfetivo,
        competencia: comp,
        fornecedor_id: ehReceita ? null : fornecedorId || null,
        recorrencia: tipo === "recorrente" ? recorrencia : null,
        renovacao: tipo === "recorrente" ? renovacao : null,
        proxima_renovacao: tipo === "recorrente" && proximaRenovacao ? proximaRenovacao : null,
        multiplas_despesas: multiplas,
        componente_fixo_centavos: !ehReceita && temVariavel ? fixo : null,
        componente_variavel_centavos: !ehReceita && temVariavel ? variavel : null,
        pct_alerta_fixo: pctFixo,
        pct_alerta_variavel: pctVariavel,
        despesas: multiplas
          ? despesas
              .filter((d) => d.nome.trim() && d.centavos)
              .map((d) => ({
                nome: d.nome.trim(),
                valor_centavos: d.centavos,
                fornecedor_id: ehReceita ? null : d.fornecedorId || null,
              }))
          : undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["execucao-linhas"] });
      void qc.invalidateQueries({ queryKey: ["visao-financeiro"] });
      void qc.invalidateQueries({ queryKey: ["painel-gestor"] });
      void qc.invalidateQueries({ queryKey: ["receita"] });
      limpar();
    },
  });

  function trocarCenario(novo: Cenario) {
    if (novo === cenario) return;
    setCenario(novo);
    // A categoria escolhida é de outra natureza; mantê-la faria o formulário
    // gravar uma linha de despesa com a tela dizendo "Receita".
    limpar();
    if (novo === "receita" && tipo === "investimento") setTipo("pontual");
    salvar.reset();
  }

  function limpar() {
    setCategoriaId("");
    setValor(null);
    setFornecedorId("");
    setMultiplas(false);
    setDespesas([{ nome: "", centavos: null, fornecedorId: "" }]);
    setTemVariavel(false);
    setFixo(null);
    setVariavel(null);
  }

  const c = cadastros.data;
  const categoriasDoCenario = c
    ? c.categorias.filter((cat) => naturezas.some((n) => n.valor === cat.natureza))
    : [];
  const exercicioEncerrado = c?.exercicios.find((e) => e.ano === exercicio)?.status === "encerrado";
  const podeSalvar =
    !!centroCustoId &&
    !!categoriaId &&
    categoriasDoCenario.some((cat) => cat.id === categoriaId) &&
    !!valorEfetivo &&
    BigInt(valorEfetivo) > 0n &&
    !exercicioEncerrado &&
    !salvar.isPending;

  return (
    <div className="pagina pb-20">
      <header className="py-7">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Orçamento
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Lançar linha orçamentária
        </h1>
        <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
          Cada linha carrega centro de custo, categoria, tipo, valor e competência. O fornecedor é
          opcional — quando o lançamento chegar do Conta Azul, o sistema o preenche sozinho.
        </p>
      </header>

      {cadastros.isPending && <p className="text-[13px] text-tinta-fraca">Carregando cadastros…</p>}

      {cadastros.isError && (
        <Vazio
          titulo="Não foi possível carregar centros de custo e categorias"
          dica={(cadastros.error as { message?: string })?.message}
        />
      )}

      {c && (
        <div className="grid gap-4 lg:grid-cols-[1.25fr_1fr]">
          {/* ── formulário ──────────────────────────────────────────────── */}
          <Painel>
            <CabecalhoGrafico titulo="Nova linha" />

            <div
              role="radiogroup"
              aria-label="Cenário da linha"
              className="mb-4 inline-flex rounded-lg border border-linha bg-superficie-2 p-1"
            >
              {CENARIOS.map((x) => (
                <button
                  key={x.valor}
                  type="button"
                  role="radio"
                  aria-checked={cenario === x.valor}
                  onClick={() => trocarCenario(x.valor)}
                  className={`rounded-md px-4 py-1.5 text-[13px] font-semibold ${
                    cenario === x.valor
                      ? "bg-superficie text-tinta shadow-sm"
                      : "text-tinta-media hover:text-tinta"
                  }`}
                >
                  {x.rotulo}
                </button>
              ))}
            </div>

            {ehReceita && (
              <p className="mb-3 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">
                Receita orçada é a meta de entrada do centro de custo. Ela não entra no teto de
                despesa, não gera alerta de estouro e aparece na aba Receita da Visão financeira.
                O realizado de receita ainda não é lido do Conta Azul.
              </p>
            )}

            {ehReceita && categoriasDoCenario.length === 0 && (
              <p className="mb-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                Nenhuma categoria de receita está disponível para orçamento. Confira em Cadastros
                se o plano de contas tem categorias de receita ativas.
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Campo rotulo="Centro de custo" obrigatorio>
                <select
                  value={centroCustoId}
                  onChange={(e) => setCentroCustoId(e.target.value)}
                  className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                >
                  <option value="">Selecione…</option>
                  {c.centros.map((cc) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.codigo} · {cc.nome}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo rotulo="Categoria financeira" obrigatorio>
                <select
                  value={categoriaId}
                  onChange={(e) => setCategoriaId(e.target.value)}
                  className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                >
                  <option value="">Selecione…</option>
                  {/* Só as naturezas do cenário escolhido: orçar receita numa
                      categoria de despesa não gera erro — gera número. Dentro
                      de Despesa, o agrupamento separa despesa de investimento. */}
                  {naturezas.map(({ valor, rotulo }) => {
                    const doGrupo = categoriasDoCenario.filter((cat) => cat.natureza === valor);
                    if (doGrupo.length === 0) return null;
                    return (
                      <optgroup key={valor} label={rotulo}>
                        {doGrupo.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.codigo ? `${cat.codigo} · ` : ""}
                            {cat.nome}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </Campo>

              <Campo rotulo="Tipo" obrigatorio ajuda={tipos.find((t) => t.valor === tipo)?.ajuda}>
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                >
                  {tipos.map((t) => (
                    <option key={t.valor} value={t.valor}>
                      {t.rotulo}
                    </option>
                  ))}
                </select>
              </Campo>

              <Campo
                rotulo="Competência"
                obrigatorio
                ajuda="Mês em que a AÇÃO acontece, não o mês do pagamento. Passagem para evento de outubro paga em setembro tem competência outubro."
              >
                <CampoCompetencia
                  competencia={comp}
                  onMudar={setComp}
                  className="num w-full cursor-pointer rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                />
              </Campo>

              {tipo === "recorrente" && (
                <>
                  <Campo
                    rotulo="Frequência"
                    ajuda={FREQUENCIAS.find((f) => f.valor === recorrencia)?.regra}
                  >
                    <select
                      value={recorrencia}
                      onChange={(e) => setRecorrencia(e.target.value)}
                      className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                    >
                      {FREQUENCIAS.map((f) => (
                        <option key={f.valor} value={f.valor}>
                          {f.rotulo}
                        </option>
                      ))}
                    </select>
                  </Campo>

                  <Campo rotulo="Renovação">
                    <select
                      value={renovacao}
                      onChange={(e) => setRenovacao(e.target.value)}
                      className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                    >
                      <option value="automatica">Automática</option>
                      <option value="manual">Manual</option>
                    </select>
                  </Campo>

                  <Campo
                    rotulo="Próxima renovação"
                    ajuda={
                      renovacao === "manual"
                        ? "Com renovação manual, o sistema avisa com antecedência — sem confirmação, o gasto sai do orçamento do ciclo seguinte."
                        : undefined
                    }
                  >
                    <input
                      type="date"
                      value={proximaRenovacao}
                      onChange={(e) => setProximaRenovacao(e.target.value)}
                      className="num w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                    />
                  </Campo>
                </>
              )}

              {!ehReceita && (
              <Campo
                rotulo="Fornecedor"
                ajuda="Opcional. Em ações sem fornecedor fechado, deixe em branco — o teto já pode ser orçado."
              >
                <select
                  value={fornecedorId}
                  onChange={(e) => setFornecedorId(e.target.value)}
                  className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[14px]"
                >
                  <option value="">Ainda não definido</option>
                  {c.fornecedores.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
              </Campo>
              )}

              {!multiplas && (
                <Campo
                  rotulo={ehReceita ? "Receita orçada" : "Valor orçado"}
                  obrigatorio
                  ajuda={ehReceita ? "Meta de entrada do período." : "Teto do período."}
                >
                  <CampoDinheiro valorCentavos={valor} onMudar={setValor} />
                </Campo>
              )}
            </div>

            {/* ── múltiplas despesas (AF-01 §6.7) ────────────────────────── */}
            <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border border-linha bg-superficie-2 px-3 py-2.5">
              <input
                type="checkbox"
                checked={multiplas}
                onChange={(e) => setMultiplas(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="text-[13.5px] font-semibold">
                  {ehReceita
                    ? "Esta categoria tem mais de uma receita neste mês?"
                    : "Esta categoria tem mais de uma despesa/ação neste mês?"}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-tinta-media">
                  {ehReceita
                    ? "Quando a mesma categoria reúne entradas distintas — duas turmas, dois contratos —, cada uma com seu valor."
                    : "Endomarketing é o caso típico: campanha, bolo de aniversariantes e brinde, cada um com seu valor. Vale para qualquer categoria com esse comportamento."}
                </span>
              </span>
            </label>

            {multiplas && (
              <div className="mt-3 rounded-lg border border-linha p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[12px] font-semibold text-tinta-media">
                    {ehReceita ? "Receitas desta categoria no mês" : "Despesas desta categoria no mês"}
                  </span>
                  <span className="num text-[12.5px] font-bold">
                    {despesas.filter((d) => d.nome.trim()).length} · {moeda(totalDespesas)}
                  </span>
                </div>

                <div className="grid gap-2">
                  {despesas.map((d, i) => (
                    <div key={i} className="grid grid-cols-[1fr_130px_auto] items-center gap-2">
                      <input
                        value={d.nome}
                        placeholder={
                          ehReceita ? "Nome da receita" : "Nome da despesa — usado no matching por texto"
                        }
                        onChange={(e) =>
                          setDespesas((ds) =>
                            ds.map((x, j) => (j === i ? { ...x, nome: e.target.value } : x)),
                          )
                        }
                        className="rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13.5px]"
                      />
                      <CampoDinheiro
                        valorCentavos={d.centavos}
                        onMudar={(v) =>
                          setDespesas((ds) => ds.map((x, j) => (j === i ? { ...x, centavos: v } : x)))
                        }
                      />
                      <button
                        type="button"
                        aria-label={ehReceita ? "Remover receita" : "Remover despesa"}
                        disabled={despesas.length === 1}
                        onClick={() => setDespesas((ds) => ds.filter((_, j) => j !== i))}
                        className="grid size-8 place-items-center rounded-lg border border-linha text-tinta-fraca hover:bg-superficie-2 disabled:opacity-40"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setDespesas((ds) => [...ds, { nome: "", centavos: null, fornecedorId: "" }])
                  }
                  className="mt-2 rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold hover:border-laranja hover:text-laranja-tinta"
                >
                  {ehReceita ? "+ Adicionar receita" : "+ Adicionar despesa"}
                </button>

                {!ehReceita && (
                  <p className="mt-2 text-[12px] text-tinta-fraca">
                    O nome entra na conciliação: quem lançar no Conta Azul deve escrever algo
                    parecido, e o sistema sugere a alocação — sem confirmar sozinho.
                  </p>
                )}
              </div>
            )}

            {/* ── componente fixo + variável (AF-01 §6.6) ────────────────── */}
            {/* Só despesa: é o alerta de fatura de ferramenta. */}
            {!ehReceita && (
            <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-linha bg-superficie-2 px-3 py-2.5">
              <input
                type="checkbox"
                checked={temVariavel}
                onChange={(e) => setTemVariavel(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="text-[13.5px] font-semibold">
                  Possui componente de uso variável (add-on)
                </span>
                <span className="mt-0.5 block text-[12.5px] text-tinta-media">
                  Ferramentas que cobram mensalidade fixa mais consumo na mesma fatura.
                </span>
              </span>
            </label>
            )}

            {!ehReceita && temVariavel && (
              <div className="mt-3 grid gap-3 rounded-lg border border-linha p-3 sm:grid-cols-2">
                <Campo rotulo="Mensalidade fixa">
                  <CampoDinheiro valorCentavos={fixo} onMudar={setFixo} />
                </Campo>
                <Campo rotulo="Teto mensal do add-on">
                  <CampoDinheiro valorCentavos={variavel} onMudar={setVariavel} />
                </Campo>
              </div>
            )}

            {/* ── limites de alerta (062) ──────────────────────────────────── */}
            {/* Valem para toda linha de despesa, não só para ferramenta com
                add-on: o motor aplica o aviso de consumo a qualquer linha e o
                de aumento a qualquer linha recorrente (009). Padrão definido
                pelo Financeiro em 15/10/2026: 80% e 30%. */}
            {!ehReceita && (
              <div className="mt-3 rounded-lg border border-linha p-3">
                <span className="text-[12px] font-semibold text-tinta-media">Alertas desta linha</span>
                <span className="mt-0.5 block text-[11.5px] text-tinta-fraca">
                  O padrão é 80% e 30%. Assinatura e mensalidade podem pedir um limite próprio.
                </span>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  <Campo
                    rotulo="Avisar quando o gasto do mês chegar a"
                    ajuda="Aviso antes do estouro. Em 100% o estouro vira pendência e o gestor justifica."
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={pctVariavel}
                        onChange={(e) => setPctVariavel(e.target.value)}
                        className="num w-20 rounded-lg border border-linha bg-superficie px-3 py-2 text-right text-[14px]"
                      />
                      <span className="text-[13px] text-tinta-fraca">% do orçado</span>
                    </div>
                  </Campo>
                  {tipo === "recorrente" && (
                    <Campo
                      rotulo="Avisar quando o mês passar"
                      ajuda="Despesa recorrente não deveria oscilar: aumento costuma ser upgrade de plano ou reajuste sem aviso."
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={999}
                          value={pctFixo}
                          onChange={(e) => setPctFixo(e.target.value)}
                          className="num w-20 rounded-lg border border-linha bg-superficie px-3 py-2 text-right text-[14px]"
                        />
                        <span className="text-[13px] text-tinta-fraca">% acima do orçado</span>
                      </div>
                    </Campo>
                  )}
                </div>
              </div>
            )}

            {exercicioEncerrado && (
              <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                O exercício {exercicio} está encerrado. Não é possível cadastrar linhas nele.
              </p>
            )}

            {salvar.isError && (
              <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                {(salvar.error as FalhaApi).message}
              </p>
            )}

            {salvar.isSuccess && (
              <p className="mt-3 rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[12.5px] text-ok-tinta">
                Linha cadastrada. O formulário está pronto para a próxima.
              </p>
            )}

            <div className="mt-4 flex items-center justify-between gap-3">
              <span className="num text-[13px] text-tinta-media">
                {valorEfetivo && BigInt(valorEfetivo) > 0n
                  ? `Total da linha: ${moeda(valorEfetivo)}`
                  : ""}
              </span>
              <button
                type="button"
                disabled={!podeSalvar}
                onClick={() => salvar.mutate()}
                className="rounded-lg bg-laranja px-5 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
              >
                {salvar.isPending ? "Salvando…" : "Cadastrar linha"}
              </button>
            </div>
          </Painel>

          {/* ── linhas já cadastradas na competência ────────────────────── */}
          <Painel>
            <CabecalhoGrafico
              titulo={`${ehReceita ? "Receita orçada" : "Linhas"} em ${competenciaLonga(comp)}`}
              dica={
                ehReceita
                  ? "A receita já orçada nesta competência, por centro de custo e categoria."
                  : "O que já está orçado nesta competência, em todos os centros de custo que você enxerga."
              }
            />
            {ehReceita && receitaDoMes.isPending && (
              <p className="text-[13px] text-tinta-fraca">Carregando…</p>
            )}
            {ehReceita && receitaDoMes.isError && (
              <Vazio
                titulo="Não foi possível carregar a receita orçada"
                dica={(receitaDoMes.error as { message?: string })?.message}
              />
            )}
            {ehReceita && receitaDoMes.data && receitaDoMes.data.linhas.length === 0 && (
              <Vazio
                titulo="Nenhuma receita orçada nesta competência"
                dica="As que você cadastrar ao lado aparecem aqui na hora."
              />
            )}
            {ehReceita && receitaDoMes.data && receitaDoMes.data.linhas.length > 0 && (
              <ul className="thin-scroll max-h-[520px] overflow-y-auto pr-1">
                {receitaDoMes.data.linhas.map((l) => (
                  <li
                    key={`${l.centro_custo_id}:${l.categoria_id}`}
                    className="border-t border-linha py-2 first:border-t-0"
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-[13.5px] font-medium" title={l.categoria_nome}>
                        {l.categoria_nome}
                      </span>
                      <span className="num shrink-0 text-[13px] font-semibold">
                        {moeda(l.orcado_centavos)}
                      </span>
                    </div>
                    <span className="mt-1 block truncate text-[11.5px] text-tinta-fraca">
                      {l.centro_custo_codigo} · {l.centro_custo_nome}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {!ehReceita && linhasDoMes.isPending && (
              <p className="text-[13px] text-tinta-fraca">Carregando…</p>
            )}
            {!ehReceita && linhasDoMes.data && linhasDoMes.data.linhas.length === 0 && (
              <Vazio
                titulo="Nenhuma linha nesta competência"
                dica="As que você cadastrar ao lado aparecem aqui na hora."
              />
            )}
            {!ehReceita && linhasDoMes.data && linhasDoMes.data.linhas.length > 0 && (
              <ul className="thin-scroll max-h-[520px] overflow-y-auto pr-1">
                {linhasDoMes.data.linhas.map((l) => (
                  <li key={l.linha_id} className="border-t border-linha py-2 first:border-t-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-[13.5px] font-medium" title={l.categoria_nome}>
                        {l.categoria_nome}
                      </span>
                      <span className="num shrink-0 text-[13px] font-semibold">
                        {moeda(l.orcado_centavos)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="truncate text-[11.5px] text-tinta-fraca">
                        {l.centro_custo_codigo} · {l.centro_custo_nome}
                        {l.fornecedor_nome ? ` · ${l.fornecedor_nome}` : ""}
                      </span>
                      <Pilula tom={TOM_STATUS[l.status] ?? "neutro"}>
                        {ROTULO_STATUS[l.status] ?? l.status}
                      </Pilula>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Painel>
        </div>
      )}

      {c && <ContratoOrcado cadastros={c} anoInicial={exercicio} />}
    </div>
  );
}

function Campo({
  rotulo,
  obrigatorio,
  ajuda,
  children,
}: {
  rotulo: string;
  obrigatorio?: boolean;
  ajuda?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-tinta-media">
        {rotulo}
        {obrigatorio && <span className="text-laranja-tinta"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
      {ajuda && <span className="mt-1 block text-[11.5px] text-tinta-fraca">{ajuda}</span>}
    </label>
  );
}
